import { useCallback, useEffect, useState } from 'react'
import {
  ActivityIndicator,
  Platform,
  RefreshControl,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View
} from 'react-native'
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons'

// styles

import { colorPalette, globalStyles } from '../../../static/styles/global'

// constants

import { WEEK_DAYS } from '../../../constants/scheduleConstants'

// components

import Card from '../../../components/shared/card/Card'
import CardSeparator from '../../../components/shared/card/CardSeparator'
import AvailabilityCard from '../../../components/schedule/AvailabilityCard'
import Tooltip from '../../../components/schedule/Tooltip'
import CustomButton from '../../../components/shared/button/Button'
import AddScheduleNote from '../../../components/schedule/AddScheduleNote'
import ScreenStatus from '../../../components/home/ScreenStatus'
import AddNote from '../../../components/schedule/AddNote'
import AddScheduleNoteReadOnly from '../../../components/schedule/AddScheduleNoteReadOnly'
import MyScrollView from '../../../components/shared/navigator/MyScrollView'
import HomeDetailsHeader from '../../../components/home/HomeDetailsHeader'
import AvailabilityCardReadOnly from '../../../components/schedule/AvailabilityCardReadOnly'
import ErrorModal from '../../../components/schedule/ErrorModal'

// types
import { ScheduleData, WeekDayLowecase } from '../../../types/scheduleTypes'

// store
import { useScheduleStore } from '../../../stores/schedule'

// skeletons
import ScheduleSkeleton from '../../../skeletons/ScheduleSkeleton'
//translation
import { useTranslation } from 'react-i18next'
import { useshiftstore } from '../../../stores/shift'
import { useLabourStore } from '../../../stores/labour'
import { useHomeStore } from '../../../stores/home'
import { AntDesign } from '@expo/vector-icons'
import Toast from 'react-native-toast-message'

const UpdateScheduleAvailability = ({ navigation, route }): JSX.Element => {
  const [toolTipVisible, setToolTipVisible] = useState(false)
  const [addScheduleNoteVisible, setAddScheduleNoteVisible] = useState(false)
  const [editScheduleNoteVisible, setEditScheduleNoteVisible] = useState(false)
  const [scheduleNote, setScheduleNote] = useState(null)
  const [savedScheduleNote, setSavedScheduleNote] = useState(null)
  const [requestedHoursLocal, setRequestedHoursLocal] = useState<string>('0')
  const [schedulingCommentLocal, setSchedulingCommentLocal] =
    useState<string>('')
  const [refreshing, setRefreshing] = useState(false)
  const [errorModalVisible, setErrorModalVisible] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [mondayAddSecondShift, setMondayAddSecondShift] = useState(false)
  const [tuesdayAddSecondShift, setTuesdayAddSecondShift] = useState(false)
  const [wednesdayAddSecondShift, setWednesdayAddSecondShift] = useState(false)
  const [thursdayAddSecondShift, setThursdayAddSecondShift] = useState(false)
  const [fridayAddSecondShift, setFridayAddSecondShift] = useState(false)
  const [saturdayAddSecondShift, setSaturdayAddSecondShift] = useState(false)
  const [sundayAddSecondShift, setSundayAddSecondShift] = useState(false)

  // params
  const { params } = route
  const { mostRecentUserOnBoardings: userId, schedules } = params
  //translation
  const { t } = useTranslation()

  // stores' data
  const {
    loading,
    error,
    scheduleData,
    setRequestedHours,
    setSchedulingComment,
    setSchedule,
    getSchedule,
    fetchData,
    updateData
  } = useScheduleStore()
  const shiftstore = useshiftstore()
  const labourstore = useLabourStore()

  const homestore = useHomeStore()

  const isWorkingHoursAvailable = (scheduleData: ScheduleData): boolean => {
    return (
      scheduleData.mondayHours !== null ||
      scheduleData.tuesdayHours !== null ||
      scheduleData.wednesdayHours !== null ||
      scheduleData.thursdayHours !== null ||
      scheduleData.fridayHours !== null ||
      scheduleData.saturdayHours !== null ||
      scheduleData.sundayHours !== null
    )
  }

  // handle save schedule note
  const handleSaveScheduleNote = (note: string) => {
    setSchedulingComment(note)
  }

  // reset scheculing
  const resetScheduling = (): void => {
    setSchedulingComment(null)
    setRequestedHours(null)
      ;[0, 1, 2, 3, 4, 5, 6].forEach((day) => {
        setSchedule(day, 'A', null, null, null, null, null)
      })
  }

  // pull down to refresh the screen
  const onRefresh = useCallback(() => {
    setRefreshing(true)
    setTimeout(() => {
      navigation.replace('ScheduleAvailabilityReadOnly', {
        refresh: Math.random()
      })
      setRefreshing(false)
    }, 1000)
  }, [])

  useEffect(() => {
    setScheduleNote('')

    fetchData(userId)
  }, [])

  useEffect(() => {
    shiftstore.fetchstatus(labourstore.labourData?.essUserKey)
  }, [])

  useEffect(() => {
    if (requestedHoursLocal) setRequestedHours(parseInt(requestedHoursLocal))
  }, [requestedHoursLocal])

  useEffect(() => {
    if (scheduleData?.requestedHours !== 0)
      setRequestedHoursLocal(scheduleData?.requestedHours?.toString())
  }, [scheduleData?.requestedHours])

  useEffect(() => {
    if (scheduleData) {
      setMondayAddSecondShift(!!scheduleData.mondayScheduleTime2Start)
      setTuesdayAddSecondShift(!!scheduleData.tuesdayScheduleTime2Start)
      setWednesdayAddSecondShift(!!scheduleData.wednesdayScheduleTime2Start)
      setThursdayAddSecondShift(!!scheduleData.thursdayScheduleTime2Start)
      setFridayAddSecondShift(!!scheduleData.fridayScheduleTime2Start)
      setSaturdayAddSecondShift(!!scheduleData.saturdayScheduleTime2Start)
      setSundayAddSecondShift(!!scheduleData.sundayScheduleTime2Start)
    }
  }, [scheduleData])

  if (loading) return <ScheduleSkeleton />

  if (error) return <ScreenStatus message={error} />

  const source = {
    essUserOnboardingKey: homestore.homeData?.essUserOnBoardingKey,
    essUserKey: labourstore.labourData?.essUserKey,
    entityId: labourstore.labourData?.entityId,
    storeId: homestore.homeData?.storeNumber,
    departmentId: homestore.homeData?.deptId
  }
  const handlesubmit = async (scheduleDataDto) => {
    const data = await shiftstore.postData(scheduleDataDto)
    if (data?.success == true) {
      await Toast.show({
        type: 'success',
        position: 'bottom',
        text1: t(data?.message),
        visibilityTime: 3000,
        autoHide: true,
        topOffset: 60,
        onHide: () => {
          navigation.replace('Onboarding')
        }
      })
    } else {
      await Toast.show({
        type: 'error',
        position: 'bottom',
        text1: t(data?.message),
        visibilityTime: 3000,
        autoHide: true,
        topOffset: 60
      })
    }
  }

  return (
    <>
      <MyScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        keyboardShouldPersistTaps={'handled'}
      >
        <>
          {scheduleData && (
            <>
              <View
                style={[
                  globalStyles.primaryBgColor,
                  globalStyles.containerBand
                ]}
              >
                <View style={[globalStyles.horizontalPadGap]}>
                  <HomeDetailsHeader
                    title={t('scheduleavailability')}
                    description={t('scheduledescription')}
                  />
                </View>
              </View>
              <View style={[globalStyles.containerOverlap, globalStyles.mb4]}>
                <Card>
                  <View
                    style={[
                      globalStyles.hDirection,
                      globalStyles.vCenterItems,
                      globalStyles.hBetweenItems
                    ]}
                  >
                    <Text
                      style={[
                        globalStyles.text,
                        globalStyles.fwBold,
                        globalStyles.fs18,
                        globalStyles.blackTextColor
                      ]}
                    >
                      {t('selecttime')}
                    </Text>
                    {/* <TouchableOpacity onPress={resetScheduling}>
                  <Text
                    style={[globalStyles.text, globalStyles.secondaryTextColor]}
                  >
                    x Clear
                  </Text>
                </TouchableOpacity> */}
                  </View>
                  <CardSeparator light={true} />
                  <View>
                    <Text
                      style={[
                        globalStyles.text,
                        globalStyles.fwSemiBold,
                        globalStyles.blackTextColor,
                        { marginBottom: 2 }
                      ]}
                    >
                      {t('yourstorelocation')} {scheduleData.storeName}
                    </Text>
                    <Text
                      style={[
                        globalStyles.text,
                        globalStyles.fs12,
                        globalStyles.secondaryTextColor,
                        globalStyles.mb3
                      ]}
                    >
                      {scheduleData.storeAddress1}
                      {scheduleData.storeAddress3 !== null &&
                        scheduleData.storeAddress3 !== '' &&
                        `, ${scheduleData.storeAddress3}`}
                    </Text>

                    {isWorkingHoursAvailable(scheduleData) && (
                      <TouchableOpacity
                        style={[
                          globalStyles.hDirection,
                          globalStyles.vCenterItems,
                          globalStyles.alignSelfLeft
                        ]}
                        onPress={() => {
                          setToolTipVisible(!toolTipVisible)
                        }}
                      >
                        <Text
                          style={[
                            globalStyles.text,
                            globalStyles.fs12,
                            globalStyles.grayTextColor,
                            globalStyles.mr1
                          ]}
                        >
                          {t('workhour')}
                        </Text>
                        <MaterialCommunityIcons
                          name="information"
                          size={18}
                          color={colorPalette.secondary}
                        />
                      </TouchableOpacity>
                    )}
                  </View>
                  <CardSeparator light={true} narrowBottom={true} />
                  <View
                    style={[
                      globalStyles.hDirection,
                      globalStyles.hBetweenItems,
                      globalStyles.vCenterItems
                    ]}
                  >
                    <Text
                      style={[
                        globalStyles.flexOne,
                        globalStyles.text,
                        globalStyles.fwBold,
                        globalStyles.primaryTextColor,
                        globalStyles.pr3
                      ]}
                    >
                      {t('requesthour')}
                    </Text>
                    <View style={[{ width: 85 }]}>
                      <TextInput
                        style={[globalStyles.input, globalStyles.inputLight]}
                        placeholder={t('number')}
                        keyboardType="numeric"
                        value={requestedHoursLocal || ''}
                        onChangeText={(text) => setRequestedHoursLocal(text)}
                        maxLength={3}
                        editable={true}
                      />
                    </View>
                  </View>
                  <CardSeparator
                    light={true}
                    narrowTop={true}
                    narrowBottom={true}
                  />
                  <AvailabilityCardReadOnly
                    day={1}
                    schedules={schedules}
                    addSecondShift={mondayAddSecondShift}
                    setAddSecondShift={setMondayAddSecondShift}
                  />
                  <CardSeparator
                    light={true}
                    narrowTop={true}
                    narrowBottom={true}
                  />
                  <AvailabilityCardReadOnly
                    day={2}
                    schedules={schedules}
                    addSecondShift={tuesdayAddSecondShift}
                    setAddSecondShift={setTuesdayAddSecondShift}
                  />
                  <CardSeparator
                    light={true}
                    narrowTop={true}
                    narrowBottom={true}
                  />
                  <AvailabilityCardReadOnly
                    day={3}
                    schedules={schedules}
                    addSecondShift={wednesdayAddSecondShift}
                    setAddSecondShift={setWednesdayAddSecondShift}
                  />
                  <CardSeparator
                    light={true}
                    narrowTop={true}
                    narrowBottom={true}
                  />
                  <AvailabilityCardReadOnly
                    day={4}
                    schedules={schedules}
                    addSecondShift={thursdayAddSecondShift}
                    setAddSecondShift={setThursdayAddSecondShift}
                  />
                  <CardSeparator
                    light={true}
                    narrowTop={true}
                    narrowBottom={true}
                  />
                  <AvailabilityCardReadOnly
                    day={5}
                    schedules={schedules}
                    addSecondShift={fridayAddSecondShift}
                    setAddSecondShift={setFridayAddSecondShift}
                  />
                  <CardSeparator
                    light={true}
                    narrowTop={true}
                    narrowBottom={true}
                  />
                  <AvailabilityCardReadOnly
                    day={6}
                    schedules={schedules}
                    addSecondShift={saturdayAddSecondShift}
                    setAddSecondShift={setSaturdayAddSecondShift}
                  />
                  <CardSeparator
                    light={true}
                    narrowTop={true}
                    narrowBottom={true}
                  />
                  <AvailabilityCardReadOnly
                    day={0}
                    schedules={schedules}
                    addSecondShift={sundayAddSecondShift}
                    setAddSecondShift={setSundayAddSecondShift}
                  />
                </Card>
                {/* {scheduleData?.schedulingComment && (
                  <AddScheduleNoteReadOnly
                    note={scheduleData?.schedulingComment}
                  />
                )} */}
                <AddScheduleNote
                  toggleModal={() => {
                    savedScheduleNote?.trim()
                      ? setEditScheduleNoteVisible(!editScheduleNoteVisible)
                      : setAddScheduleNoteVisible(!addScheduleNoteVisible)
                  }}
                  note={scheduleData?.schedulingComment}
                  mode={savedScheduleNote?.trim() ? 'edit' : 'add'}
                />


                {shiftstore.shiftstatusData?.statusCode == 0 ? (
                  <>
                    <View
                      style={{ justifyContent: 'center', alignItems: 'center' }}
                    >
                      <Text style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        <AntDesign name="infocirlce" size={20} color="#ED8B22" />{' '}
                        {t(shiftstore.shiftstatusData?.message)}
                      </Text>
                    </View>
                  </>
                ) : (
                  <></>
                )}

                {shiftstore.loading ? (
                  <ActivityIndicator />
                ) : (
                  shiftstore.shiftstatusData?.statusCode != 0 &&
                  (
                    <View style={[globalStyles.mt2]}>
                      <CustomButton
                        text={t('submitavailability')}
                        onPress={() => {
                          const {
                            essUserOnBoardingKey,
                            brandingName,
                            storeName,
                            storeShortName,
                            storeAddress1,
                            storeAddress2,
                            storeAddress3,
                            mondayHours,
                            tuesdayHours,
                            wednesdayHours,
                            thursdayHours,
                            fridayHours,
                            saturdayHours,
                            sundayHours,
                            scheduleStatusValue,
                            statusDescription,
                            ...scheduleDataDto
                          } = scheduleData

                          let isScheduleValid = true

                          for (const day of WEEK_DAYS) {
                            let weekDay = day.toLowerCase() as WeekDayLowecase
                            let selectedScheduleValue =
                              scheduleDataDto[`${weekDay}ScheduleValue`]

                            // applied only on "Select Availabilty Hours"
                            if (selectedScheduleValue === 'S') {
                              // first shift
                              if (
                                !scheduleDataDto[
                                `${weekDay}ScheduleTime1Start`
                                ] &&
                                !scheduleDataDto[`${weekDay}ScheduleTime1End`]
                              ) {
                                isScheduleValid = false
                                setErrorMessage(
                                  `Please, select “First Start Time” on ${day}.`
                                )
                                setErrorModalVisible(!errorModalVisible)
                                return
                              }

                              if (
                                scheduleDataDto[`${weekDay}ScheduleTime1Start`] &&
                                !scheduleDataDto[`${weekDay}ScheduleTime1End`]
                              ) {
                                isScheduleValid = false
                                setErrorMessage(
                                  `Please, select “First End Time” on ${day}.`
                                )
                                setErrorModalVisible(!errorModalVisible)
                                return
                              }

                              let dayAddSecondShift: boolean

                              switch (weekDay) {
                                case 'monday':
                                  dayAddSecondShift = mondayAddSecondShift
                                  break
                                case 'tuesday':
                                  dayAddSecondShift = tuesdayAddSecondShift
                                  break
                                case 'wednesday':
                                  dayAddSecondShift = wednesdayAddSecondShift
                                  break
                                case 'thursday':
                                  dayAddSecondShift = thursdayAddSecondShift
                                  break
                                case 'friday':
                                  dayAddSecondShift = fridayAddSecondShift
                                  break
                                case 'saturday':
                                  dayAddSecondShift = saturdayAddSecondShift
                                  break
                                case 'sunday':
                                  dayAddSecondShift = sundayAddSecondShift
                                  break
                              }

                              // second shift
                              if (
                                dayAddSecondShift &&
                                !scheduleDataDto[
                                `${weekDay}ScheduleTime2Start`
                                ] &&
                                !scheduleDataDto[`${weekDay}ScheduleTime2End`]
                              ) {
                                isScheduleValid = false
                                setErrorMessage(
                                  `Please, select “Second Start Time” on ${day}.`
                                )
                                setErrorModalVisible(!errorModalVisible)
                                return
                              }

                              if (
                                scheduleDataDto[`${weekDay}ScheduleTime2Start`] &&
                                !scheduleDataDto[`${weekDay}ScheduleTime2End`]
                              ) {
                                isScheduleValid = false
                                setErrorMessage(
                                  `Please, select “Second End Time” on ${day}.`
                                )
                                setErrorModalVisible(!errorModalVisible)
                                return
                              }
                            }
                          }

                          // save to database (check if the status is not complete first)
                          if (

                            isScheduleValid
                          ) {
                            Object.assign(scheduleDataDto, source)
                            handlesubmit(scheduleDataDto)
                          }
                        }}
                        color={'success'}
                      />
                    </View>
                  )
                )}
              </View>
              {isWorkingHoursAvailable(scheduleData) && (
                <Tooltip
                  title={t('workhour')}
                  visible={toolTipVisible}
                  setVisible={setToolTipVisible}
                  workingHours={{
                    mondayHours: scheduleData.mondayHours,
                    tuesdayHours: scheduleData.tuesdayHours,
                    wednesdayHours: scheduleData.wednesdayHours,
                    thursdayHours: scheduleData.thursdayHours,
                    fridayHours: scheduleData.fridayHours,
                    saturdayHours: scheduleData.saturdayHours,
                    sundayHours: scheduleData.sundayHours
                  }}
                />
              )}


              <AddNote
                title={t('schedulenote')}
                visible={addScheduleNoteVisible}
                setVisible={setAddScheduleNoteVisible}
                note={scheduleNote}
                setNote={setScheduleNote}
                savedNote={savedScheduleNote}
                setSavedNote={setSavedScheduleNote}
                handleSaveNote={handleSaveScheduleNote}
                mode="add"
              />
              <AddNote
                title={t('schedulenote')}
                visible={editScheduleNoteVisible}
                setVisible={setEditScheduleNoteVisible}
                note={scheduleNote}
                setNote={setScheduleNote}
                savedNote={savedScheduleNote}
                setSavedNote={setSavedScheduleNote}
                handleSaveNote={handleSaveScheduleNote}
                mode="edit"
              />

            </>
          )}
        </>
      </MyScrollView>

    </>
  )
}

export default UpdateScheduleAvailability
